'use client'

import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import Link from 'next/link'
import { ArrowRight, Trophy } from 'lucide-react'
import { competitionsApi, PublicCompetition } from '@/lib/api'

export default function RegisterPage() {
  const [competitions, setCompetitions] = useState<PublicCompetition[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    competitionsApi.listPublic().then(setCompetitions).catch(console.error).finally(() => setLoading(false))
  }, [])

  if (loading)
    return (
      <div className="p-6 flex justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    )

  return (
    <main className="flex min-h-screen flex-col">
      {/* Navigation */}
      <nav className="flex items-center justify-between border-b border-border px-6 py-4">
        <Link href="/" className="text-2xl font-bold text-primary">
          Cosmicon Events
        </Link>
        <div className="flex gap-4">
          <Button variant="ghost" asChild>
            <Link href="/">Home</Link>
          </Button>
          <Button variant="ghost" asChild>
            <Link href="/events">Events</Link>
          </Button>
        </div>
      </nav>

      {/* Main Content */}
      <div className="flex-1 px-6 py-12">
        <div className="mb-12 text-center">
          <h1 className="text-4xl font-bold">Join a Competition</h1>
          <p className="mt-3 text-lg text-muted-foreground">
            Select a competition to register and showcase your cosplay talent
          </p>
        </div>

        {/* Info Banner */}
        <Card className="mb-8 bg-primary/5 border-primary/20 p-4">
          <p className="text-sm">
            <strong>Protected Registration:</strong> This form is protected with CAPTCHA
            to prevent spam. Your information will be kept secure and only shared with
            event organizers.
          </p>
        </Card>

        {/* Competitions Grid */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-2 max-w-4xl mx-auto">
          {competitions.map((comp) => {
            const isOpen = comp.status === 'registration_open'
            return (
              <Card key={comp.id} className="flex flex-col p-6 hover:shadow-lg transition-shadow">
                <div className="mb-4 flex items-start justify-between">
                  <div className="flex-1">
                    <h2 className="text-xl font-semibold">{comp.name}</h2>
                    <p className="mt-1 text-sm text-muted-foreground">{comp.eventName}</p>
                  </div>
                  <Trophy className="h-5 w-5 text-primary" />
                </div>

                <div className="mb-4 space-y-2 text-sm flex-1">
                  {comp.eventDate && (
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Date</span>
                      <span className="font-semibold">
                        {new Date(comp.eventDate).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Registered</span>
                    <span className="font-semibold">{comp.currentContestantNumber}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Status</span>
                    <span
                      className={`font-semibold ${
                        isOpen ? 'text-green-600' : 'text-destructive'
                      }`}
                    >
                      {isOpen ? 'Open' : 'Closed'}
                    </span>
                  </div>
                </div>

                <Button
                  asChild={isOpen}
                  disabled={!isOpen}
                  className="w-full"
                >
                  {isOpen ? (
                    <Link href={`/register/${comp.id}`}>
                      Register
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  ) : (
                    <>
                      Register
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </Card>
            )
          })}
        </div>

        {/* No Competitions */}
        {competitions.length === 0 && (
          <div className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-border py-12">
            <Trophy className="mb-4 h-12 w-12 text-muted-foreground" />
            <p className="text-muted-foreground">
              No competitions available at this time
            </p>
            <p className="text-sm text-muted-foreground">
              Check back later or contact organizers for more information
            </p>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="border-t border-border bg-muted/30 px-6 py-8 text-center text-sm text-muted-foreground">
        <p>&copy; 2024 Cosmicon Events</p>
      </footer>
    </main>
  )
}
